/**
 * API Client with Axios
 * Configured with interceptors for auth and error handling
 */
import axios from 'axios';
import toast from 'react-hot-toast';
import { API_BASE_URL } from '../utils/constants';
import { storage } from '../utils/storage';

export const apiClient = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor - Add auth token
apiClient.interceptors.request.use(
  (config) => {
    const token = storage.getAccessToken();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor - Handle errors and token refresh
apiClient.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    // If 401 and we haven't tried to refresh yet
    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;

      try {
        // The refresh token always belongs to the real (admin) session — it's
        // never swapped in when impersonating, since impersonation tokens are
        // access-token-only. So a refresh while impersonating means the
        // impersonation token expired and this refresh is falling back to the
        // admin's own session, not renewing the impersonation one.
        const wasImpersonating = !!localStorage.getItem('admin_original_token');
        const refreshToken = storage.getRefreshToken();
        if (refreshToken) {
          // Try to refresh the token
          const response = await axios.post(`${API_BASE_URL}/api/auth/refresh`, {
            refresh_token: refreshToken,
          });

          const { access_token } = response.data;
          storage.setAccessToken(access_token);

          if (wasImpersonating) {
            // End the impersonation flow explicitly instead of silently
            // continuing on the admin's own session while the UI still
            // claims to be impersonating.
            localStorage.removeItem('admin_original_token');
            toast.error('Impersonation session expired');
            window.location.href = '/admin/users';
            return Promise.reject(error);
          }

          // Retry the original request
          originalRequest.headers.Authorization = `Bearer ${access_token}`;
          return apiClient(originalRequest);
        }
      } catch (refreshError) {
        // Refresh failed, clear auth and redirect to login
        storage.clearAuth();
        window.location.href = '/login';
        return Promise.reject(refreshError);
      }
    }

    return Promise.reject(error);
  }
);

